import type { Option, StepData } from '@kizen/filters/types';
import type { DropdownProps, SelectOption } from './types';
import { useCallback, useMemo } from 'react';
import { get } from 'lodash';
import Select from '__components/Inputs/Select';
import { useFilterOptions } from '../hooks/useFilterOptions';

type Group = {
  label: string;
  order: number;
  options: Option[];
};

type GroupedDropdownStaticProps = DropdownProps & {
  option_groups?: StepData['option_groups'];
};

export const GroupedDropdownStatic = ({
  step,
  width,
  url,
  method,
  next,
  placeholder,
  options: optionsProp,
  value,
  onChange,
  result_path,
  value_id_path,
  option_filter,
  option_paths,
  option_groups,
  body,
  params,
  endAdornment,
  error,
  disabled,
  isStatus = false,
}: GroupedDropdownStaticProps) => {
  const v =
    value_id_path && value && typeof value === 'object'
      ? get(value, value_id_path)
      : value;

  const { isLoading, options, lookup, onMenuOpen, onMenuClose } =
    useFilterOptions(
      method,
      url,
      result_path,
      value_id_path,
      option_paths,
      option_filter,
      body,
      params,
      optionsProp,
      value
    );

  const { groupedOptions, groupedLookup } = useMemo(() => {
    if (!option_groups) {
      return { groupedOptions: options, groupedLookup: lookup };
    }

    const optionsByValue = options.reduce<Record<string, Option>>(
      (acc, option) => {
        acc[option.value] = option;
        return acc;
      },
      {}
    );

    const groupedLookup = new Map<string, Option>();

    const groupedOptions = option_groups.map((group) => {
      const opt: Group = {
        label: group.label,
        order: group.order,
        options: [],
      };

      for (const v of group.values) {
        if (optionsByValue[v]) {
          opt.options.push(optionsByValue[v]);
          groupedLookup.set(v, optionsByValue[v]);
        }
      }

      return opt;
    });

    return { groupedOptions, groupedLookup };
  }, [option_groups, lookup, options]);

  const handleChange = useCallback(
    (opt: SelectOption) => {
      onChange(step, {
        next: opt.next ?? next,
        value: lookup.has(opt.value) ? lookup.get(opt.value)! : opt.value,
      });
    },
    [step, next, lookup, onChange]
  );

  return (
    <Select
      disabled={disabled}
      width={width}
      value={v ? groupedLookup.get(v) : undefined}
      options={groupedOptions}
      isColored={isStatus}
      placeholder={placeholder}
      onChange={handleChange}
      endAdornment={endAdornment}
      error={error}
      loadItems={isLoading}
      onMenuClose={onMenuClose}
      onMenuOpen={onMenuOpen}
    />
  );
};
